import React, { ChangeEvent, useCallback, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import bytes from 'bytes'
import * as Sentry from '@sentry/react'
import { selectUser } from '../../../modules/user/selectors'
import { getDimensions } from '../../../shared_components/Missions/Types/Upload'
import { theseus2 } from '../../../api/theseus'
import { stopPropagation } from '../../../shared_components/EmbeddableModal'

import { Box } from '../../../shared_components/UI/Box'
import AnimatedLoader from '../../../shared_components/AnimatedLoader'
import useWeb3 from '../../../hooks/useWeb3'
import useIsWalletConnected from '../../../shared_components/Header/useIsWalletConnected'
import ThemedButton from '../../../shared_components/ThemedButton'
import { ImageUploaderCardBox } from '../../../shared_components/Library/Card'
import { P, Span } from '../../../shared_components/Library/Typeography'
import XClose from '../../../shared_components/Icons/XClose'
import { ImageCropModal, getAspectRatio, getImageDimensions, useResizedImageUpload } from './ImageCrop'

const ImageUploader = ({
  label,
  subLabel = '',
  imageUrl,
  setImageUrl,
  width = '800',
  height = '800',
}) => {
  const isConnected = useIsWalletConnected()
  const { initWeb3 } = useWeb3()

  const hiddenFileInput = useRef<HTMLInputElement>(null)
  const [filename, setFilename] = useState('')
  const [fileSize, setFileSize] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  const user = useSelector(selectUser)
  const { id: userId } = user ?? {}

  const handleUpload = async (file: File, width?: number, height?: number) => {
    setIsSubmitting(true)
    if (!file) return
    if (!userId) throw new Error('No account or task id')

    setFilename(file.name)
    setFileSize(bytes(file.size))

    const formData = new FormData()
    formData.append('image', file)
    formData.append('name', `task-proof-${new Date().getTime()}`)

    if (width && height) {
      formData.append('width', width.toString())
      formData.append('height', height.toString())
    } else {
      const { width, height } = await getDimensions(file)
      formData.append('width', width.toString())
      formData.append('height', height.toString())
    }

    try {
      const response = await theseus2.post(
        `/project/upload/69/generic/b2c/${userId}`,
        formData,
      )

      const {
        data: { uploadedImage },
      } = response

      setImageUrl(uploadedImage.url)
    } catch (response) {
      Sentry.captureException(response)
      console.error(response)
      toast.error('Something went wrong')
      setErrors([response.data?.message])
    } finally {
      setIsSubmitting(false)
    }
  }

  const {
    imageToResize,
    setImageToResize,
    setIsResizingImage,
    setResizedImage,
  } = useResizedImageUpload(handleUpload)
  
  const startUpload = (e: any) => {
    e.target.value = null
    stopPropagation(e)
    e.preventDefault()
    if (isSubmitting || !hiddenFileInput?.current) return
    const blank = new DataTransfer()
    hiddenFileInput.current.files = blank.files
    hiddenFileInput.current.click()
  }

  const onImageChange = useOnImageChange({
    width,
    height,
    name: 'tokenImage',
    handleUpload,
    setImageToResize,
    setIsResizingImage,
    isLogo: true,
  })

  const removeImage = () => {
    setImageUrl('')
    setFilename('')
    setFileSize('')
    hiddenFileInput.current.value = ''
  }

  return (
    <Container>
      <input
        type="file"
        accept="image/png,image/jpg,image/jpeg,image/gif,image/webp"
        value={''}
        className="file-uploader"
        onChange={onImageChange}
        ref={hiddenFileInput}
      />

      <Box dc g={16}>
        <Box dc g={4}>
          {label}
          {!!subLabel && <P s="S" c="text-secondary">{subLabel}</P>}
        </Box>

        {imageUrl && (
          <ImageUploaded
            imageUrl={imageUrl}
            filename={filename}
            fileSize={fileSize}
            onRemove={removeImage}
          />
        )}

        {!imageUrl && (
          <>
            {!isConnected ? (
              <ThemedButton
                onClick={initWeb3}
                text="Connect"
                $styleType="tertiary"
              />
            ) : (
              <ThemedButton
                onClick={startUpload}
                disabled={isSubmitting}
                text={
                  isSubmitting ? (
                    <>
                      <AnimatedLoader onlyWhite size={20} />
                      Uploading...
                    </>
                  ) : (
                    'Upload an image'
                  )
                }
                $styleType="tertiary"
              />
            )}
          </>
        )}
      </Box>
      {imageToResize && (
        <ImageCropModal
          imgSrc={imageToResize.url}
          setImageToResize={setImageToResize}
          setResizedImage={setResizedImage}
          desiredHeight={Number(height)}
          desiredWidth={Number(width)}
        />
      )}
    </Container>
  )
}

function ImageUploaded({
  imageUrl,
  filename,
  fileSize,
  onRemove,
}: {
  imageUrl: string
  filename: string
  fileSize: string
  onRemove: () => void
}) {
  return (
    <ImageUploaderCardBox dr pad="m">
      <Box g={8} minW="160px">
        <img src={imageUrl} alt="Uploaded image" />
        <Box dc g={2}>
          <Span s="L" c="text-primary" fw="semibold" wba twb>
            {filename}
          </Span>
          <Span s="S" c="text-secondary">
            {fileSize}
          </Span>
        </Box>
      </Box>
      <ThemedButton
        onClick={onRemove}
        text={
          <>
            <XClose size={20} />
            <span>Remove</span>
          </>
        }
        $styleType="tertiary"
      />
    </ImageUploaderCardBox>
  )
}

type OnImageChangeProps = {
  width?: string
  height?: string
  name: string
  handleUpload: (file: File, width?: number, height?: number) => void
  setImageToResize?: ({
    url,
    width,
    height,
  }: {
    url: string
    width: number
    height: number
  }) => void
  setIsResizingImage?: (v: boolean) => void
  isLogo: boolean
}
export function useOnImageChange({
  width,
  height,
  name,
  handleUpload,
  setImageToResize,
  setIsResizingImage,
  isLogo,
}: OnImageChangeProps) {
  return useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      try {
        if (!e.target.files) throw new Error('No files found')
        const file = e.target.files[0]

        const { width: actualWidth, height: actualHeight } =
          await getImageDimensions(file)

        if (setImageToResize && setIsResizingImage) {
          setIsResizingImage(isLogo)

          const desiredApectRatio = getAspectRatio(
            Number(width),
            Number(height),
          )
          const actualAspectRatio = getAspectRatio(actualWidth, actualHeight)

          console.log({ desiredApectRatio, actualAspectRatio })
          
          if (desiredApectRatio !== actualAspectRatio) {
            setImageToResize({
              url: URL.createObjectURL(file),
              width: actualWidth,
              height: actualHeight,
            })

            return
          }
        }

        const formData = new FormData()
        formData.append('name', name)
        formData.append('image', file)

        let finalWidth = actualWidth , finalHeight = actualHeight

        if (width && height) {
          finalHeight = Number(height)
          finalWidth = Number(width)
        } else {
          if (actualWidth > 800) {
            const ratio = actualWidth / actualHeight
            const newWidth = 800
            const newHeight = newWidth / ratio
            finalHeight = newHeight
            finalWidth = newWidth
          } else {
            finalHeight = actualHeight
            finalWidth = actualWidth
          }
        }

        handleUpload(file, finalHeight, finalWidth)
      } catch (err) {
        console.error(err)
        toast.error('Error uploading image pre API')
      } finally {
        if (!e.target) return
        e.target.value = ''
      }
    },
    [
      width,
      height,
      name,
      handleUpload,
      setImageToResize,
      setIsResizingImage,
      isLogo,
    ],
  )
}

const Container = styled(Box)`
  align-items: center;
  gap: 32px;
  .loader {
    width: 120px;
    height: 120px;
    max-width: 120px;
    img {
      /* width: 100%; */
    }
  }
  input.file-uploader {
    display: none;
  }
`

export default ImageUploader
